import { useInvoiceState } from './useInvoiceState';
import { useLineItemActions } from './useLineItemsActions';
import { useInvoiceEditorStore } from '@/modules/billing/invoice/services/useInvoiceEditorStore.js';
import { useCompanyStore } from '@/services/UserService/useCompanyStore';
import { storeToRefs } from 'pinia';
import { useMeStore } from '@/services/UserService/useMeStore';
import { useSnackbarHandler } from '@/composables/useSnackbarHandler';
export function useGroupActions() {
  const invoiceState = useInvoiceState();
  const {
    sortGroup
  } = useInvoiceEditorStore();
  const {
    companySettings
  } = storeToRefs(useCompanyStore());
  const meStore = useMeStore();
  const {
    handleSuccess
  } = useSnackbarHandler();
  const {
    makeNonBillableLineItem,
    makeLineItemBillable,
    writeDownLineItemToZero,
    constructDescription
  } = useLineItemActions();
  const groupTypeNameDictionary = {
    billableEntries: 'Services',
    nonBillableEntries: 'Services (Not Billed)',
    combinedEntries: 'Services',
    fixedFees: 'Fixed Fees',
    fixedFeeEntries: 'Fixed Fee Detail',
    expenses: 'Expenses',
    adjustments: 'Adjustments',
    fees: 'Fees'
  };

  // Compute invoiceFormatSettings lazily to ensure invoiceState is seeded
  const getInvoiceFormatSettings = () => {
    return Object.keys(invoiceState.format).length > 0 ? invoiceState.format : invoiceState.client?.invoice || companySettings?.value?.invoice;
  };
  const createGroupName = (groupType, matterId) => {
    let matterName = '';
    let groupTypeName = groupTypeNameDictionary[groupType];
    if (invoiceState.matters.length) {
      let matter = invoiceState.matters.find(m => m.id === matterId);
      if (matter) {
        matterName = matter.name;
      }
      return `${matterName} (${groupTypeName})`;
    }
    return groupTypeName;
  };
  const moveLineItemsToGroup = (lineItems, groupToMoveTo) => {
    if (!lineItems || !lineItems.length) return;

    //remove from current group
    lineItems.forEach(lineItem => {
      const currentGroup = invoiceState.groups.find(group => group.lineItems.includes(lineItem));
      if (currentGroup) {
        // Remove the line item from the current group
        currentGroup.lineItems = currentGroup.lineItems.filter(item => item !== lineItem);
      }
    });
    if (needToCombineEntries(lineItems, groupToMoveTo)) {
      const formatSettings = getInvoiceFormatSettings();
      let lineItemsToPush = [];
      lineItems.forEach(li => {
        let matchingDateLineItem = groupToMoveTo.lineItems.find(gli => isSameDay(li.date, gli.date));
        if (matchingDateLineItem) {
          matchingDateLineItem.entries.push(...li.entries);
          matchingDateLineItem.description = constructDescription(matchingDateLineItem, {
            includeMatterName: formatSettings?.includeMatter,
            includeInitials: formatSettings?.includeUser,
            includeHours: formatSettings?.combineHours,
            includeCodes: formatSettings?.includeCodes,
            matter: groupToMoveTo.matter
          });
        } else {
          lineItemsToPush.push(li);
        }
      });
    } else {
      groupToMoveTo.lineItems.push(...lineItems);
    }
    sortGroup(groupToMoveTo);
    invoiceState.updateLineItemsTotals();
  };
  const needToCombineEntries = (newLineItems, groupToMoveTo) => {
    let combine = false;
    const formatSettings = getInvoiceFormatSettings();
    if (groupToMoveTo.type.indexOf('ntries') > -1 && formatSettings?.combine === true) {
      let groupDates = groupToMoveTo.lineItems.map(x => x.date);
      let newDates = newLineItems.map(x => x.date);
      combine = newDates.some(date1 => {
        return groupDates.some(date2 => {
          return isSameDay(date1, date2);
        });
      });
    }
    return combine;
  };
  const isSameDay = (d1, d2) => {
    let date1 = new Date(d1);
    let date2 = new Date(d2);
    return date1.getFullYear() === date2.getFullYear() && date1.getMonth() === date2.getMonth() && date1.getDate() === date2.getDate();
  };
  const makeMultipleLineItemsBillable = async lineItems => {
    if (!lineItems || !lineItems.length) return;
    for (let lineItem of lineItems) {
      await makeLineItemBillable(lineItem);
    }
    const newGroupType = invoiceState.format.combineBillable === true ? 'combinedEntries' : 'billableEntries';

    // Group line items by matter ID
    const lineItemsByMatter = groupLineItemsByMatter(lineItems);
    Object.keys(lineItemsByMatter).forEach(matterId => {
      const itemsForMatter = lineItemsByMatter[matterId];
      const matter = itemsForMatter[0]?.entries?.[0]?.matter;
      var groupToMoveTo = findOrCreateGroup(newGroupType, matter);
      moveLineItemsToGroup(itemsForMatter, groupToMoveTo);
    });
    unCheckAll(false);
  };
  const makeMultipleLineItemsNonBillable = lineItems => {
    if (!lineItems || !lineItems.length) return;

    // Update each line item to be non-billable
    lineItems.forEach(lineItem => {
      makeNonBillableLineItem(lineItem);
    });
    const newGroupType = invoiceState.format.combineBillable === true ? 'combinedEntries' : 'nonBillableEntries';
    // Group line items by matter ID
    const lineItemsByMatter = groupLineItemsByMatter(lineItems);
    Object.keys(lineItemsByMatter).forEach(matterId => {
      const itemsForMatter = lineItemsByMatter[matterId];
      const matter = itemsForMatter[0]?.entries?.[0]?.matter;
      const groupToMoveTo = findOrCreateGroup(newGroupType, matter);
      moveLineItemsToGroup(itemsForMatter, groupToMoveTo);
    });
    unCheckAll(false);
  };
  const writeDownMultipleLineItemsToZero = lineItems => {
    lineItems.forEach(writeDownLineItemToZero);
    invoiceState.updateLineItemsTotals();
    unCheckAll(false);
  };
  function unCheckAll(alertMessage = true) {
    invoiceState.groups.forEach(group => {
      group.groupChecked = false;
      group.lineItems.filter(item => item.entries).forEach(line => {
        line.checked = false;
      });
    });
    if (alertMessage) {
      handleSuccess('The invoice was edited successfully');
    }
  }
  const groupLineItemsByMatter = lineItems => {
    return lineItems.reduce((acc, lineItem) => {
      const matterId = lineItem.entries?.[0]?.matter?.id || 'unknown';
      if (!acc[matterId]) {
        acc[matterId] = [];
      }
      acc[matterId].push(lineItem);
      return acc;
    }, {});
  };
  const findMissingGroup = (groupType, matterId) => {
    return invoiceState.missingGroups.find(g => g.type === groupType && g.matter.id === matterId);
  };
  const findExistingGroup = (groupType, matterId) => {
    return invoiceState.groups.find(g => g.type === groupType && g.matter.id === matterId);
  };
  const findOrCreateGroup = (groupType, matter) => {
    let existingGroup = findExistingGroup(groupType, matter.id);
    if (!existingGroup) {
      existingGroup = findMissingGroup(groupType, matter.id);
      if (existingGroup) {
        invoiceState.addGroup(existingGroup);
      }
    }
    if (!existingGroup) {
      let newGroupName = createGroupName(groupType, matter.id);
      existingGroup = {
        name: newGroupName,
        type: groupType,
        matter,
        lineItems: []
      };
      invoiceState.addGroup(existingGroup);
    }
    return existingGroup;
  };
  const assignLineItemsToFixedFee = (lineItems, fixedFeeLineItem) => {
    let account = meStore.me;
    if (!lineItems || !fixedFeeLineItem) return;
    const charge = fixedFeeLineItem.charges[0];

    // Update each line item to be assigned to the fixed fee
    lineItems.forEach(lineItem => {
      const currentGroup = invoiceState.groups.find(group => group.lineItems.includes(lineItem));

      // Ensure previous group is preserved
      const rate = {
        amount: 0,
        label: '',
        edited: true
      }; // Reset the line item rate
      lineItem.amount = 0;
      lineItem.rate = rate;
      lineItem.entries = lineItem.entries.map(entry => {
        // Create base updated entry
        const updatedEntry = {
          ...entry,
          billing: {
            type: 'fixed'
          },
          billingType: 'fixedFee',
          billedHours: entry.hours,
          matter: charge.matter,
          chargeId: charge.id
        };

        // Only change rate for non-billable entries
        if (entry.billingType === 'nonBillable') {
          // For non-billable entries, change billing type to fixedFee
          // but maintain the original rate structure
          let userMatterRate = {
            label: 'Standard',
            amount: account.rates?.standard || 0
          };
          updatedEntry.rate = userMatterRate;
        }
        return updatedEntry;
      });
      lineItem.status = 'updated';
    });

    // Find or create the fixed fee details group
    const fixedFeeDetailsGroup = findOrCreateGroup('fixedFeeEntries', charge.matter);
    moveLineItemsToGroup(lineItems, fixedFeeDetailsGroup);
    unCheckAll(false);
  };

  /**
   * Saves multiple line items for later by marking them as 'savedForLater' and removing them from the invoice
   * @param {Array} lineItems - The line items to save for later
   * @param {string} sourceGroupType - The type of group the line items are coming from.
   * Valid values include: 'billableEntries', 'nonBillableEntries', 'combinedEntries',
   * 'fixedFees', 'fixedFeeEntries', 'expenses', 'adjustments', 'fees'.
   * This parameter determines whether to preserve relationships between line items and charges,
   * specifically for fixed fee related sections.
   */
  const saveMultipleLineItemsForLater = (lineItems, sourceGroupType) => {
    lineItems.forEach(lineItem => {
      lineItem.status = 'savedForLater';

      // Skip cascading removals for Fixed Fee Detail → Fixed Fee direction
      // (removing a detail entry should NOT cascade up to remove the parent charge)
      // But removing a Fixed Fee SHOULD cascade down to remove associated detail entries
      const preserveRelationships = sourceGroupType === 'fixedFeeEntries';
      if (lineItem.charges && !preserveRelationships) {
        //check for entries associated with this fixed fee
        const chargeIds = new Set(lineItem.charges.map(c => c.id));
        const processedLineItemIds = new Set();
        invoiceState.groups.forEach(group => {
          group.lineItems.forEach(li => {
            if (processedLineItemIds.has(li.id)) return;
            if (li.entries) {
              const hasMatchingEntry = li.entries.some(e => e.chargeId && chargeIds.has(e.chargeId));
              if (hasMatchingEntry) {
                li.status = 'savedForLater';
                invoiceState.removeLineItem(li);
                processedLineItemIds.add(li.id);
              }
            }
          });
        });
      } else if (lineItem.entries) {
        const processedEntryLineItemIds = new Set();
        lineItem.entries.forEach(entry => {
          // Mark entry as not billed before saving
          entry.billed = false;
          if (entry.chargeId && !preserveRelationships) {
            invoiceState.groups.forEach(group => {
              group.lineItems.forEach(li => {
                if (processedEntryLineItemIds.has(li.id)) return;
                if (li.charges && li.charges.some(c => c.id == entry.chargeId)) {
                  li.status = 'savedForLater';
                  invoiceState.removeLineItem(li);
                  processedEntryLineItemIds.add(li.id);
                } else if (li.entries && li.entries.some(e => e.chargeId == entry.chargeId)) {
                  li.status = 'savedForLater';
                  invoiceState.removeLineItem(li);
                  processedEntryLineItemIds.add(li.id);
                }
              });
            });
          }
        });
      }

      // Mark charges as not billed
      if (lineItem.charges) {
        lineItem.charges.forEach(charge => {
          charge.billed = false;
        });
      }
      invoiceState.removeLineItem(lineItem);
    });
    unCheckAll(false);
  };
  const makeMultipleLineItemsBilled = lineItems => {
    lineItems.forEach(lineItem => {
      lineItem.status = 'markedAsBilled';
      lineItem.entries.forEach(entry => {
        entry.billed = true;
        entry.billedHours = 0;
      });
      invoiceState.removeLineItem(lineItem, 'markedAsBilled');
    });
  };
  return {
    moveLineItemsToGroup,
    makeMultipleLineItemsBillable,
    makeMultipleLineItemsNonBillable,
    makeMultipleLineItemsBilled,
    writeDownMultipleLineItemsToZero,
    assignLineItemsToFixedFee,
    saveMultipleLineItemsForLater,
    unCheckAll,
    findOrCreateGroup
  };
}